import React, { useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Container,
  SxProps,
  Theme,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { fonts, colors } from "../../styles/theme";
import { ButtonDensityContext } from "../common/inputs/Button";

interface SectionProps {
  title?: string; // Optional: Titel der Section
  headerContent?: React.ReactNode; // Optional: Benutzerdefinierter Header-Inhalt (ersetzt Titel)
  children: React.ReactNode;
  actions?: React.ReactNode; // Optional: Buttons oder andere Aktionen
  actionsPosition?: "right" | "center"; // Optional: Position der Actions (Standard: 'right')
  container?: boolean; // Optional: Container um die Section
  noPadding?: boolean; // Optional: Kein Padding für Tabellen
  noInnerPadding?: boolean; // Optional: Kein inneres Padding, aber Design beibehalten
  noOuterPadding?: boolean; // Optional: Kein äußeres Padding für die gesamte Section
  /**
   * Optional: Der Inhalt scrollt innerhalb der Kachel, die Bildlaufleiste liegt an ihrer rechten
   * Kante – wie bei Tabellen mit `maxHeight`. Die Höhe gibt der Aufrufer über `sx` vor.
   */
  scrollable?: boolean;
  rowSpacing?: number; // Optional: Abstand zwischen Reihen (Standard: 3)
  sx?: SxProps<Theme>; // Optional: Custom styles für das Paper-Element
  collapsible?: boolean; // Optional: Section kann auf-/zugeklappt werden
  defaultExpanded?: boolean; // Optional: Standardzustand (Standard: true = aufgeklappt)
  hasWarning?: boolean; // Optional: Zeigt ein Warnungs-Icon neben dem Titel
  warningTooltip?: string; // Optional: Tooltip für das Warnungs-Icon
}

/**
 * Wiederverwendbare Komponente für Unterabschnitte mit Paper-Kachel
 * Überschrift und Aktionen sind über der Kachel, Inhalt ist in der Kachel
 * Optional aufklappbar (collapsible)
 */
const Section: React.FC<SectionProps> = ({
  title,
  headerContent,
  children,
  actions,
  actionsPosition = "right",
  container = false,
  noPadding = false,
  noInnerPadding = false,
  noOuterPadding = false,
  scrollable = false,
  sx,
  collapsible = false,
  defaultExpanded = true,
  hasWarning = false,
  warningTooltip,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Einheitliches Innenpadding für den Inhalt
  const contentPadding = noPadding || noInnerPadding ? 0 : 3; // 3 = 24px (Theme-Spacing)

  const handleToggle = () => {
    if (collapsible) {
      setExpanded(!expanded);
    }
  };

  const content = (
    <Box sx={{ mb: noOuterPadding ? 0 : 3 }}>
      {/* Header mit Titel/headerContent und Aktionen - nur anzeigen wenn vorhanden */}
      {(title || headerContent || actions) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            cursor: collapsible ? "pointer" : "default",
          }}
          onClick={handleToggle}
        >
          {/* Linke Spalte: headerContent oder Titel (bei center mit flex: 1 für symmetrisches Layout) */}
          {/* Icon steht rechts neben dem Label, damit alle Labels linksbündig auf gleicher Höhe stehen,
              unabhängig davon ob die Section collapsible ist oder nicht */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: actionsPosition === "center" ? 1 : "none",
            }}
          >
            {/* headerContent hat Vorrang vor title */}
            {headerContent ? (
              <>
                {headerContent}
                {/* Warning-Icon bei headerContent */}
                {hasWarning && (
                  <Tooltip
                    title={
                      warningTooltip || "Es gibt Warnungen in diesem Bereich"
                    }
                    arrow
                  >
                    <WarningAmberIcon
                      sx={{
                        color: colors.state.highlight,
                        fontSize: "1.25rem",
                        ml: 1,
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </>
            ) : title ? (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: fonts.weights.light,
                    color: "text.primary",
                    mb: 0,
                  }}
                >
                  {title}
                </Typography>
                {/* Warning-Icon neben dem Titel */}
                {hasWarning && (
                  <Tooltip
                    title={
                      warningTooltip || "Es gibt Warnungen in diesem Bereich"
                    }
                    arrow
                  >
                    <WarningAmberIcon
                      sx={{
                        color: colors.state.highlight,
                        fontSize: "1.25rem",
                        ml: 1,
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </>
            ) : null}
            {/* Expand/Collapse Icon nur bei collapsible, rechts neben dem Label */}
            {collapsible && (
              <IconButton
                size="small"
                sx={{
                  color: expanded ? "primary.main" : "text.secondary",
                  opacity: 0.5,
                  p: 0.5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggle();
                }}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
          {/* Mittlere/Rechte Spalte: Actions */}
          {/* Bündig mit der rechten Kachelkante, wie der Titel links – nicht eingerückt um das
              Innenpadding, sonst stünden die Knöpfe je nach Kachel an anderer Stelle. Knöpfe im
              Kopf sind kompakt (ButtonDensityContext). */}
          {actions && (
            <Box
              onClick={(e) => e.stopPropagation()} // Verhindert Toggle bei Klick auf Actions
            >
              <ButtonDensityContext.Provider value="compact">
                {actions}
              </ButtonDensityContext.Provider>
            </Box>
          )}
          {/* Rechte Spalte: Spacer für symmetrisches Layout bei center */}
          {actionsPosition === "center" && <Box sx={{ flex: 1 }} />}
        </Box>
      )}

      {/* Paper-Kachel mit Inhalt - bei collapsible in Collapse gewrappt */}
      <Collapse
        in={!collapsible || expanded}
        timeout="auto"
        unmountOnExit={false}
      >
        <Paper
          elevation={2}
          sx={{
            // Beim Scrollen trägt der Bildlaufbereich das Innenpadding, nicht die Kachel – sonst
            // stünde die Bildlaufleiste um das Padding eingerückt im Inhalt.
            p: scrollable ? 0 : contentPadding,
            borderRadius: 2,
            overflow: scrollable ? "hidden" : "visible",
            ...(scrollable ? { display: "flex", flexDirection: "column" } : {}),
            backgroundColor: "background.paper",
            boxShadow: `0 4px 12px ${colors.shadow.light}`,
            margin: 0,
            transition:
              "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-0.5px)",
              boxShadow: `0 4px 16px ${colors.shadow.medium}`,
            },
            ...sx,
          }}
        >
          {scrollable ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                p: contentPadding,
              }}
            >
              {children}
            </Box>
          ) : (
            children
          )}
        </Paper>
      </Collapse>
    </Box>
  );

  // Optional: Container um die Section
  if (container) {
    return <Container maxWidth={false}>{content}</Container>;
  }

  return content;
};

export default Section;
